import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { accountingApi, type AccountSearchOptionDto } from "../services/accountingApi";

type AccountOption = AccountSearchOptionDto;

const ALL_ACCOUNTS_ID = "__ALL_ACCOUNTS__";

interface AccountAsyncSelectProps {
  valueId?: string;
  onChange: (accountId?: string) => void;
  postingOnly?: boolean;
  activeOnly?: boolean;
  pageSize?: number;
  includeAllOption?: boolean;
  disabled?: boolean;
}

export function AccountAsyncSelect({
  valueId,
  onChange,
  postingOnly = true,
  activeOnly = true,
  pageSize = 20,
  includeAllOption = true,
  disabled = false,
}: AccountAsyncSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState<AccountOption | null>(null);

  const debouncedSearchText = useDebounce(searchText, 300);

  const allAccountsOption = useMemo<AccountOption>(() => ({
    id: ALL_ACCOUNTS_ID,
    code: "ALL",
    name: "All accounts",
    type: "",
    isPosting: true,
  }), []);

  const searchQuery = useQuery({
    queryKey: ["accounts", "search", debouncedSearchText, postingOnly, activeOnly, pageSize],
    queryFn: () => accountingApi.searchAccounts({
      query: debouncedSearchText || undefined,
      postingOnly,
      activeOnly,
      pageSize,
      pageNumber: 1,
    }),
    enabled: open,
  });

  useEffect(() => {
    if (!valueId) return;

    if (selected?.id === valueId) return;

    let cancelled = false;
    accountingApi.getAccountById(valueId)
      .then((account) => {
        if (cancelled) return;
        setSelected({
          id: account.id,
          code: account.accountCode,
          name: account.accountName,
          type: account.accountTypeName,
          isPosting: !account.isGroup,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setSelected(includeAllOption ? allAccountsOption : null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [valueId, includeAllOption, allAccountsOption, selected?.id]);

  const options = useMemo(() => {
    const accountOptions = searchQuery.data ?? [];
    return includeAllOption ? [allAccountsOption, ...accountOptions] : accountOptions;
  }, [searchQuery.data, includeAllOption, allAccountsOption]);

  const effectiveSelected = !valueId
    ? includeAllOption
      ? allAccountsOption
      : null
    : selected?.id === valueId
      ? selected
      : null;

  const displayText = effectiveSelected
    ? effectiveSelected.id === ALL_ACCOUNTS_ID
      ? "All accounts"
      : `${effectiveSelected.code} ${effectiveSelected.name}`
    : includeAllOption
      ? "All accounts"
      : "Select account";

  const handleSelect = (option: AccountOption) => {
    if (option.id === ALL_ACCOUNTS_ID) {
      setSelected(allAccountsOption);
      onChange(undefined);
      setOpen(false);
      return;
    }

    setSelected(option);
    onChange(option.id);
    setOpen(false);
  };

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={6}
          className="z-[60] w-[--radix-popover-trigger-width] p-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search account code or name..."
              value={searchText}
              onValueChange={setSearchText}
            />
            <CommandList>
              <CommandEmpty>
                {searchQuery.isLoading ? "Loading accounts..." : "No accounts found"}
              </CommandEmpty>
              <CommandGroup className="max-h-64 overflow-y-auto">
                {searchQuery.isLoading && (
                  <div className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading accounts...
                  </div>
                )}

                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={`${option.code} ${option.name}`}
                    onSelect={() => handleSelect(option)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        effectiveSelected?.id === option.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.id === ALL_ACCOUNTS_ID ? "All accounts" : `${option.code} ${option.name}`}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected && selected.id !== ALL_ACCOUNTS_ID && (
        <div className="text-xs text-muted-foreground">Selected: {selected.code} {selected.name}</div>
      )}
    </div>
  );
}
